import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type FormEvent,
  type ReactNode,
  type SetStateAction,
} from "react";
import {
  Check,
  Copy,
  Eye,
  EyeOff,
  History,
  Loader2,
  PencilLine,
  Plus,
  Search,
  ShieldCheck,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import {
  SfRowMenu,
  SfRowMenuBtn,
} from "@/components/RecordChrome";
import { cn } from "@/lib/cn";
import {
  bulkSecretAction,
  createSecretV2,
  deleteSecret,
  friendlyAuthError,
  loadSecretVersions,
  patchSecretV2,
  patchVaultSettings,
  restoreSecretVersion,
  type SecretVersionRow,
} from "@/lib/api";
import { displaySecret, parseTagInput, type SecretRow } from "@/lib/secretTypes";

type Vault = {
  id: string;
  record_id?: string;
  name: string;
  description: string | null;
  organization_id: string;
  /** When true, secret changes in this store file an approval (4-eyes) for other admins. */
  requires_approval?: boolean;
};

type MemberRow = {
  membership_id?: string;
  membership_record_id?: string;
  user_id: string;
  user_record_id?: string;
  role: string;
  profiles: { display_name: string | null } | null;
};

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked */
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="rounded-md p-1 text-[var(--color-text-muted)] transition hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
      aria-label={copied ? "Copied" : "Copy"}
      title={copied ? "Copied" : "Copy"}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-[var(--color-success)]" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline justify-between text-[0.78rem] font-medium text-[var(--color-text)]">
        <span>{label}</span>
        {hint ? (
          <span className="text-[11px] font-normal text-[var(--color-text-subtle)]">{hint}</span>
        ) : null}
      </span>
      {children}
    </label>
  );
}

function Card({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-[var(--color-text)]">{title}</h3>
          {description ? (
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">{description}</p>
          ) : null}
        </div>
        {action ? <div className="flex-shrink-0">{action}</div> : null}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ children }: { children: ReactNode }) {
  return (
    <p className="py-6 text-center text-sm text-[var(--color-text-subtle)]">{children}</p>
  );
}

export function SecretsSection({
  orgId,
  vaults,
  secrets,
  vaultForSecret,
  setVaultForSecret,
  secretName,
  setSecretName,
  secretPlain,
  setSecretPlain,
  secretFilters,
  setSecretFilters,
  draftServerQ,
  setDraftServerQ,
  draftTagTerms,
  setDraftTagTerms,
  localNameFilter,
  setLocalNameFilter,
  reloadSecrets,
  members = [],
  canManageVaults = false,
  aclUser,
  setAclUser,
  aclVault,
  setAclVault,
  aclPerm,
  setAclPerm,
  grantAcl,
  showToast,
}: {
  orgId: string;
  vaults: Vault[];
  secrets: SecretRow[];
  vaultForSecret: string;
  setVaultForSecret: (v: string) => void;
  secretName: string;
  setSecretName: (v: string) => void;
  secretPlain: string;
  setSecretPlain: (v: string) => void;
  secretFilters: { q: string; vaultId: string; tagTerms: string };
  setSecretFilters: Dispatch<
    SetStateAction<{ q: string; vaultId: string; tagTerms: string }>
  >;
  draftServerQ: string;
  setDraftServerQ: (v: string) => void;
  draftTagTerms: string;
  setDraftTagTerms: (v: string) => void;
  localNameFilter: string;
  setLocalNameFilter: (v: string) => void;
  reloadSecrets: () => Promise<void>;
  members?: MemberRow[];
  canManageVaults?: boolean;
  aclUser: string;
  setAclUser: (v: string) => void;
  aclVault: string;
  setAclVault: (v: string) => void;
  aclPerm: "read" | "read_write" | "admin";
  setAclPerm: (v: "read" | "read_write" | "admin") => void;
  grantAcl: (e: FormEvent) => Promise<void>;
  showToast: (message: string, kind?: "ok" | "err") => void;
}) {
  const [newTags, setNewTags] = useState("");
  const [revealMap, setRevealMap] = useState<Record<string, boolean>>({});
  const [editingSecretId, setEditingSecretId] = useState<string | null>(null);
  const [editPlain, setEditPlain] = useState("");
  const [editTags, setEditTags] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTagInput, setBulkTagInput] = useState("");
  const [bulkUntagInput, setBulkUntagInput] = useState("");
  const [bulkVaultId, setBulkVaultId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const [historyFor, setHistoryFor] = useState<SecretRow | null>(null);
  const [historyRows, setHistoryRows] = useState<SecretVersionRow[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErr, setHistoryErr] = useState<string | null>(null);
  const [restoreBusy, setRestoreBusy] = useState<number | null>(null);
  const [vaultPatchBusy, setVaultPatchBusy] = useState<string | null>(null);

  const vaultName = useCallback(
    (id: string) => vaults.find((v) => v.id === id)?.name ?? id.slice(0, 8),
    [vaults],
  );

  useEffect(() => {
    setSelected(new Set());
  }, [orgId, secrets]);

  useEffect(() => {
    if (editingSecretId && !secrets.some((x) => x.id === editingSecretId)) {
      setEditingSecretId(null);
      setEditPlain("");
      setEditTags("");
      setEditBusy(false);
    }
  }, [secrets, editingSecretId]);

  async function saveNewSecret(e: FormEvent) {
    e.preventDefault();
    if (!vaultForSecret || !secretName.trim()) return;
    const tags = parseTagInput(newTags);
    if (tags === null) {
      showToast("Tags must be lowercase letters, numbers, dots, dashes, or underscores.", "err");
      return;
    }
    try {
      await createSecretV2({
        orgId,
        vaultId: vaultForSecret,
        name: secretName.trim(),
        value: secretPlain,
        metadata: { encoding: "server_aes_gcm" },
        tags,
      });
      setSecretName("");
      setSecretPlain("");
      setNewTags("");
      showToast("Secret saved.", "ok");
      await reloadSecrets();
    } catch (err: unknown) {
      showToast(friendlyAuthError(err), "err");
    }
  }

  async function submitEdit(s: SecretRow) {
    if (!editPlain.trim()) return;
    const tags = parseTagInput(editTags);
    if (tags === null) {
      showToast("Invalid tag format.", "err");
      return;
    }
    setEditBusy(true);
    try {
      const r = await patchSecretV2({
        orgId,
        secretId: s.id,
        value: editPlain.trim(),
        metadata: { encoding: "server_aes_gcm" },
        tags,
      });
      if (r.approval_required) {
        showToast(friendlyAuthError(new Error("approval_required")), "err");
      } else {
        showToast("Secret updated.", "ok");
        setEditingSecretId(null);
        setEditPlain("");
        setEditTags("");
      }
      await reloadSecrets();
    } catch (err: unknown) {
      showToast(friendlyAuthError(err), "err");
    } finally {
      setEditBusy(false);
    }
  }

  async function openHistory(s: SecretRow) {
    setHistoryFor(s);
    setHistoryRows(null);
    setHistoryErr(null);
    setHistoryLoading(true);
    try {
      const rows = await loadSecretVersions(orgId, s.id);
      setHistoryRows(rows);
    } catch (err: unknown) {
      setHistoryErr(friendlyAuthError(err));
    } finally {
      setHistoryLoading(false);
    }
  }

  async function toggleVaultApproval(vaultId: string, requiresApproval: boolean) {
    setVaultPatchBusy(vaultId);
    try {
      await patchVaultSettings(orgId, vaultId, { requires_approval: requiresApproval });
      showToast(
        requiresApproval
          ? "This store now requires approval for sensitive secret changes."
          : "This store allows immediate secret updates again.",
        "ok",
      );
      await reloadSecrets();
    } catch (err: unknown) {
      showToast(friendlyAuthError(err), "err");
    } finally {
      setVaultPatchBusy(null);
    }
  }

  async function onRestore(version: number) {
    if (!historyFor) return;
    if (
      !window.confirm(
        `Restore version ${version}? This creates a new version from that snapshot.`,
      )
    ) {
      return;
    }
    setRestoreBusy(version);
    try {
      await restoreSecretVersion(orgId, historyFor.id, version);
      showToast(`Restored from v${version}.`, "ok");
      setHistoryFor(null);
      await reloadSecrets();
    } catch (err: unknown) {
      showToast(friendlyAuthError(err), "err");
    } finally {
      setRestoreBusy(null);
    }
  }

  async function onDeleteOne(s: SecretRow) {
    if (!window.confirm(`Delete secret “${s.name}”? This cannot be undone.`)) return;
    try {
      const r = await deleteSecret(orgId, s.id);
      if (r.approval_required) {
        showToast(friendlyAuthError(new Error("approval_required")), "err");
      } else {
        showToast("Secret deleted.", "ok");
      }
      await reloadSecrets();
    } catch (err: unknown) {
      showToast(friendlyAuthError(err), "err");
    }
  }

  const filteredLocal = useMemo(() => {
    const q = localNameFilter.trim().toLowerCase();
    if (!q) return secrets;
    return secrets.filter((s) => s.name.toLowerCase().includes(q));
  }, [secrets, localNameFilter]);

  const allVisibleSelected =
    filteredLocal.length > 0 && filteredLocal.every((s) => selected.has(s.id));

  function toggleSelectAllVisible() {
    if (allVisibleSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredLocal.map((s) => s.id)));
    }
  }

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function runBulk(action: "delete" | "tag" | "untag" | "move") {
    const ids = [...selected];
    if (!ids.length) return;
    if (action === "delete") {
      if (
        !window.confirm(`Delete ${ids.length} secret(s)? This cannot be undone.`)
      ) {
        return;
      }
    }
    setBulkBusy(true);
    try {
      if (action === "tag" || action === "untag") {
        const raw = action === "tag" ? bulkTagInput : bulkUntagInput;
        const tags = parseTagInput(raw);
        if (!tags?.length) {
          showToast("Enter at least one valid tag.", "err");
          setBulkBusy(false);
          return;
        }
        await bulkSecretAction({ orgId, action, secret_ids: ids, tags });
        showToast(action === "tag" ? "Tags applied." : "Tags removed.", "ok");
      } else if (action === "move") {
        if (!bulkVaultId) {
          showToast("Choose a target store.", "err");
          setBulkBusy(false);
          return;
        }
        await bulkSecretAction({
          orgId,
          action: "move",
          secret_ids: ids,
          vault_id: bulkVaultId,
        });
        showToast("Secrets moved.", "ok");
      } else {
        await bulkSecretAction({ orgId, action: "delete", secret_ids: ids });
        showToast("Secrets deleted.", "ok");
      }
      setSelected(new Set());
      await reloadSecrets();
    } catch (err: unknown) {
      showToast(friendlyAuthError(err), "err");
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      {vaults.length > 0 && (
        <Card
          title="Add secret"
          description={
            vaults.length > 1
              ? "Choose which store holds this secret (encrypted at rest on the API server)."
              : "Stored in your workspace default store — encrypted at rest on the API server."
          }
        >
          <form
            onSubmit={saveNewSecret}
            className={cn(
              "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:items-end",
              vaults.length > 1 ? "lg:grid-cols-5" : "lg:grid-cols-4",
            )}
          >
            {vaults.length > 1 ? (
              <Field label="Store">
                <select
                  className="field-input"
                  value={vaultForSecret}
                  onChange={(e) => setVaultForSecret(e.target.value)}
                >
                  {vaults.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            <Field label="Name">
              <input
                required
                placeholder="STRIPE_KEY"
                className="field-input font-mono"
                value={secretName}
                onChange={(e) => setSecretName(e.target.value)}
              />
            </Field>
            <Field label="Value">
              <input
                required
                placeholder="sk_live_…"
                className="field-input font-mono"
                value={secretPlain}
                onChange={(e) => setSecretPlain(e.target.value)}
              />
            </Field>
            <Field label="Tags" hint="optional, comma-separated">
              <input
                className="field-input font-mono text-xs"
                placeholder="prod, billing"
                value={newTags}
                onChange={(e) => setNewTags(e.target.value)}
              />
            </Field>
            <button type="submit" className="btn btn-primary">
              <Plus className="h-4 w-4" /> Save secret
            </button>
          </form>
          <p className="mt-3 text-[11px] text-[var(--color-text-muted)]">
            Machine API keys authenticate automation without interactive MFA; scope keys narrowly and rotate if leaked.
          </p>
        </Card>
      )}

      {canManageVaults && vaults.length > 0 ? (
        <Card
          title="Approval policy (per store)"
          description="When enabled, updating or deleting secrets in that store files an approval for another admin instead of applying immediately."
        >
          <ul className="divide-y divide-[var(--color-border)] rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
            {vaults.map((v) => (
              <li
                key={v.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
              >
                <span className="font-medium text-[var(--color-text)]">{v.name}</span>
                <label className="flex cursor-pointer items-center gap-2 text-xs text-[var(--color-text-muted)]">
                  {vaultPatchBusy === v.id ? (
                    <Loader2 className="h-3.5 w-3.5 shrink-0 spin text-[var(--color-accent)]" />
                  ) : null}
                  <input
                    type="checkbox"
                    className="rounded border-[var(--color-border)]"
                    checked={Boolean(v.requires_approval)}
                    disabled={vaultPatchBusy === v.id}
                    onChange={(e) => void toggleVaultApproval(v.id, e.target.checked)}
                  />
                  Require second admin approval
                </label>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card
        title={`Secrets (${secrets.length} loaded)`}
        description="Server filters reload from the API. The quick filter only narrows the rows already loaded."
        action={
          <div className="relative min-w-[200px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-subtle)]" />
            <input
              className="control-input min-w-[200px] pl-7"
              placeholder="Quick filter (name)"
              value={localNameFilter}
              onChange={(e) => setLocalNameFilter(e.target.value)}
              aria-label="Filter loaded secrets by name"
            />
          </div>
        }
      >
        <div className="mb-4 flex flex-col gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 sm:flex-row sm:flex-wrap sm:items-end">
          <Field label="Search (server)">
            <input
              className="field-input min-w-[180px] text-xs"
              placeholder="Full-text on name…"
              value={draftServerQ}
              onChange={(e) => setDraftServerQ(e.target.value)}
            />
          </Field>
          <Field label="Tags (all must match)">
            <input
              className="field-input min-w-[180px] text-xs"
              placeholder="prod, api"
              value={draftTagTerms}
              onChange={(e) => setDraftTagTerms(e.target.value)}
            />
          </Field>
          <Field label="Store">
            <select
              className="field-input text-xs"
              value={secretFilters.vaultId}
              onChange={(e) => {
                const v = e.target.value;
                setSecretFilters((prev) => ({ ...prev, vaultId: v }));
              }}
            >
              <option value="">All stores</option>
              {vaults.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </Field>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-primary text-xs"
              onClick={() => {
                setSecretFilters({
                  q: draftServerQ.trim(),
                  vaultId: secretFilters.vaultId,
                  tagTerms: draftTagTerms,
                });
              }}
            >
              Apply filters
            </button>
            <button
              type="button"
              className="btn btn-secondary text-xs"
              onClick={() => {
                setDraftServerQ("");
                setDraftTagTerms("");
                setSecretFilters({ q: "", vaultId: "", tagTerms: "" });
              }}
            >
              Clear
            </button>
          </div>
        </div>

        {selected.size > 0 ? (
          <div className="mb-4 flex flex-col gap-3 rounded-lg border border-[var(--color-accent)] bg-[var(--color-accent-soft)] p-3 text-xs">
            <div className="font-semibold text-[var(--color-accent-hover)]">
              {selected.size} selected
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <input
                className="field-input max-w-[200px]"
                placeholder="tags to add"
                value={bulkTagInput}
                onChange={(e) => setBulkTagInput(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-secondary px-2 py-1 text-[11px]"
                disabled={bulkBusy}
                onClick={() => void runBulk("tag")}
              >
                <Tag className="h-3 w-3" /> Tag
              </button>
              <input
                className="field-input max-w-[200px]"
                placeholder="tags to remove"
                value={bulkUntagInput}
                onChange={(e) => setBulkUntagInput(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-secondary px-2 py-1 text-[11px]"
                disabled={bulkBusy}
                onClick={() => void runBulk("untag")}
              >
                Untag
              </button>
              <select
                className="field-input max-w-[160px] text-[11px]"
                value={bulkVaultId}
                onChange={(e) => setBulkVaultId(e.target.value)}
              >
                <option value="">Move to…</option>
                {vaults.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-secondary px-2 py-1 text-[11px]"
                disabled={bulkBusy}
                onClick={() => void runBulk("move")}
              >
                Move
              </button>
              <button
                type="button"
                className="btn btn-secondary px-2 py-1 text-[11px] text-[var(--color-danger)]"
                disabled={bulkBusy}
                onClick={() => void runBulk("delete")}
              >
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            </div>
          </div>
        ) : null}

        {filteredLocal.length === 0 ? (
          <EmptyState>
            {secrets.length === 0
              ? "No secrets match these filters — add one above or adjust filters."
              : "No secrets match the current list filter."}
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="dl-table">
              <caption className="sr-only">
                Workspace secrets; select rows for bulk actions or open the row menu for history and edit.
              </caption>
              <thead>
                <tr>
                  <th className="w-10">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      aria-label="Select all visible secrets"
                      className="rounded border-[var(--color-border)]"
                    />
                  </th>
                  <th>Name</th>
                  <th>Store</th>
                  <th>Tags</th>
                  <th>Ver.</th>
                  <th>Value</th>
                  <th>Updated</th>
                  <th className="w-12 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLocal.map((s) => {
                  const shown = !!revealMap[s.id];
                  const decoded = displaySecret(s);
                  const isEditing = editingSecretId === s.id;
                  return (
                    <tr key={s.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(s.id)}
                          onChange={() => toggleRow(s.id)}
                          aria-label={`Select ${s.name}`}
                          className="rounded border-[var(--color-border)]"
                        />
                      </td>
                      <td className="font-mono text-xs">{s.name}</td>
                      <td className="text-xs text-[var(--color-text-muted)]">
                        {vaultName(s.vault_id)}
                      </td>
                      <td className="max-w-[140px]">
                        <div className="flex flex-wrap gap-1">
                          {(s.tags ?? []).map((t) => (
                            <span
                              key={t}
                              className="rounded-full bg-[var(--color-surface-3)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)]"
                            >
                              {t}
                            </span>
                          ))}
                          {(s.tags ?? []).length === 0 ? (
                            <span className="text-[10px] text-[var(--color-text-subtle)]">—</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="font-mono text-xs text-[var(--color-text-muted)]">
                        v{s.version}
                      </td>
                      <td className="min-w-[220px]">
                        {isEditing ? (
                          <div className="flex flex-col gap-2">
                            <input
                              autoFocus
                              type="password"
                              autoComplete="new-password"
                              placeholder="New value"
                              className="field-input min-w-0 font-mono text-xs"
                              value={editPlain}
                              onChange={(e) => setEditPlain(e.target.value)}
                              disabled={editBusy}
                            />
                            <input
                              className="field-input font-mono text-xs"
                              placeholder="tags (comma-separated)"
                              value={editTags}
                              onChange={(e) => setEditTags(e.target.value)}
                              disabled={editBusy}
                            />
                            <div className="flex shrink-0 gap-1">
                              <button
                                type="button"
                                className="btn btn-primary whitespace-nowrap px-3 py-1.5 text-xs"
                                disabled={editBusy || !editPlain.trim()}
                                onClick={() => void submitEdit(s)}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary whitespace-nowrap px-3 py-1.5 text-xs"
                                disabled={editBusy}
                                onClick={() => {
                                  setEditingSecretId(null);
                                  setEditPlain("");
                                  setEditTags("");
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <code className="max-w-[min(280px,40vw)] truncate font-mono text-xs">
                              {shown ? decoded : "•".repeat(Math.min(decoded.length, 16))}
                            </code>
                            <button
                              type="button"
                              onClick={() =>
                                setRevealMap((m) => ({ ...m, [s.id]: !m[s.id] }))
                              }
                              className="rounded-md p-1 text-[var(--color-text-muted)] transition hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
                              aria-label={shown ? "Hide" : "Show"}
                            >
                              {shown ? (
                                <EyeOff className="h-3.5 w-3.5" />
                              ) : (
                                <Eye className="h-3.5 w-3.5" />
                              )}
                            </button>
                            <CopyButton value={decoded} />
                          </div>
                        )}
                      </td>
                      <td className="text-xs text-[var(--color-text-subtle)]">
                        {new Date(s.updated_at).toLocaleString()}
                      </td>
                      <td className="text-right">
                        <SfRowMenu ariaLabel={`Actions for ${s.name}`}>
                          <SfRowMenuBtn
                            onClick={() => {
                              void openHistory(s);
                            }}
                          >
                            <span className="flex items-center gap-2">
                              <History className="h-3 w-3" /> Version history
                            </span>
                          </SfRowMenuBtn>
                          <SfRowMenuBtn
                            onClick={() => {
                              setEditingSecretId(s.id);
                              setEditPlain("");
                              setEditTags((s.tags ?? []).join(", "));
                            }}
                          >
                            <span className="flex items-center gap-2">
                              <PencilLine className="h-3 w-3" /> Edit value / tags
                            </span>
                          </SfRowMenuBtn>
                          <SfRowMenuBtn onClick={() => void onDeleteOne(s)}>
                            <span className="flex items-center gap-2 text-[var(--color-danger)]">
                              <Trash2 className="h-3 w-3" /> Delete
                            </span>
                          </SfRowMenuBtn>
                        </SfRowMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {canManageVaults && (
        <Card
          title="Staff access to secrets"
          description="Grant client staff access per secret store. Invite staff under Organization → Add client staff first."
        >
          <form
            onSubmit={grantAcl}
            className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:items-end"
          >
            <Field label="Client staff">
              <select
                className="field-input"
                value={aclUser}
                onChange={(e) => setAclUser(e.target.value)}
              >
                <option value="">Select user</option>
                {members
                  .filter((m) => m.role === "client_staff")
                  .map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.profiles?.display_name ?? m.user_id.slice(0, 8)}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Store">
              <select
                className="field-input"
                value={aclVault}
                onChange={(e) => setAclVault(e.target.value)}
              >
                <option value="">Select store</option>
                {vaults.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Permission">
              <select
                className="field-input"
                value={aclPerm}
                onChange={(e) => setAclPerm(e.target.value as typeof aclPerm)}
              >
                <option value="read">read</option>
                <option value="read_write">read / write</option>
                <option value="admin">admin</option>
              </select>
            </Field>
            <button type="submit" className="btn btn-primary" disabled={!aclUser || !aclVault}>
              <ShieldCheck className="h-4 w-4" /> Grant access
            </button>
          </form>
        </Card>
      )}

      {historyFor ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/35 px-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="secret-history-title"
          onClick={(ev) => {
            if (ev.target === ev.currentTarget) setHistoryFor(null);
          }}
        >
          <div
            className="relative max-h-[85vh] w-full max-w-lg overflow-hidden rounded-2xl bg-[var(--color-surface)]"
            style={{ boxShadow: "var(--shadow-modal)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="absolute right-3 top-3 rounded-md p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-surface-3)]"
              onClick={() => setHistoryFor(null)}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="border-b border-[var(--color-border)] px-5 py-4">
              <h2 id="secret-history-title" className="pr-10 text-lg font-semibold">
                Version history
              </h2>
              <p className="mt-1 font-mono text-xs text-[var(--color-text-muted)]">
                {historyFor.name}
              </p>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              {historyErr ? (
                <p className="text-sm text-[var(--color-danger)]">{historyErr}</p>
              ) : historyLoading ? (
                <div className="flex justify-center py-8 text-[var(--color-text-muted)]">
                  <Loader2 className="h-6 w-6 spin" />
                </div>
              ) : historyRows && historyRows.length === 0 ? (
                <p className="text-sm text-[var(--color-text-muted)]">No versions yet.</p>
              ) : (
                <ul className="space-y-2">
                  {(historyRows ?? []).map((row) => (
                    <li
                      key={row.id}
                      className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 text-xs"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-semibold text-[var(--color-text)]">
                          v{row.version}
                        </span>
                        <span className="text-[var(--color-text-muted)]">
                          {new Date(row.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-1 text-[var(--color-text-muted)]">
                        {row.actor_email ?? row.actor_display_name ?? "—"}
                        {row.comment ? ` · ${row.comment}` : ""}
                      </p>
                      {row.version !== historyFor.version ? (
                        <button
                          type="button"
                          className="btn btn-secondary mt-2 px-2 py-1 text-[11px]"
                          disabled={restoreBusy === row.version}
                          onClick={() => void onRestore(row.version)}
                        >
                          {restoreBusy === row.version ? (
                            <Loader2 className="h-3 w-3 spin" />
                          ) : null}{" "}
                          Restore this version
                        </button>
                      ) : (
                        <p className="mt-2 text-[10px] text-[var(--color-text-subtle)]">
                          Current head version
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
